import {useQueries} from '@tanstack/react-query'

import {
  SlotValidationResult,
  useSlotManagementContext,
  UseSlotValidationParams
} from '../../../slotManagement'
import {composeSlotValidationCallbacks} from '../../../slotManagement/utils/composeSlotValidationCallbacks'

import {useOrderIntakeSlotValidationEnabled} from './useOrderIntakeSlotValidationEnabled'

/**
 * Order Intake slot validation hook.
 * Validates ALL dateTimes in the form when called.
 *
 * Gets configuration from SlotManagementContext (feature toggle, orgUnitId, siteNumber).
 *
 * Validation is disabled when:
 * - Feature toggle is off
 * - Slot request params cannot be built (validation failed)
 * - No query function provided
 *
 * Side effect: Updates slot availability state in context based on validation results.
 * Consumer callbacks (onSuccess/onError) are preserved and composed with internal state updates.
 */
export const useOrderIntakeSlotValidation = ({
  slotValidationQueryFn
}: UseSlotValidationParams): SlotValidationResult[] => {
  const {slotAvailabilities, setSlotAvailability} = useSlotManagementContext()

  // Determine if validation should be enabled
  const {isEnabled, slotRequestResults} = useOrderIntakeSlotValidationEnabled()

  // Determine if we should validate
  const shouldValidate = isEnabled && !!slotValidationQueryFn

  // Create query options for each valid slot request
  // Use onSuccess/onError callbacks to update state reactively
  const queryOptions = slotRequestResults.map((slotRequest, index) => {
    if (!shouldValidate || !slotRequest.isValid || !slotRequest.params) {
      return {
        queryKey: ['slot-validation-disabled'],
        queryFn: () => ({valid: false}),
        enabled: false
      }
    }

    const baseOptions = slotValidationQueryFn(slotRequest.params)
    return composeSlotValidationCallbacks(baseOptions, index, setSlotAvailability)
  })

  // Execute all queries in parallel
  const queries = useQueries({queries: queryOptions})
  return queries.map((query, index) => ({
    query,
    ...slotAvailabilities[index]
  }))
}
